import { z } from 'zod'
import { fallback } from '../../../utilities/zod-utils'
import {
	colorSchema,
	metaGeneralContentDefault,
	metaGeneralContentSchema,
	metaOverlayDefault,
	metaOverlaySchema,
} from './common'
import { metaLogoDefault, metaLogoSchema } from './common/meta-logo'
import {
	defaultSubtitleTextLayerConfiguration,
	defaultTitleTextLayerConfiguration,
	metaDefaultSubtitleTextLayerSchema,
	metaDefaultTitleTextLayerSchema,
} from './common/meta-text-layer'
import { metaTransformDefault, metaTransformSchema } from './common/meta-transform'
import { feedAdCardsDefault, feedAdCardsSchema } from './feed-ad-cards'
import { feedAdPositioningDefault, feedAdPositioningSchema } from './feed-ad-positioning'

export type UnconformedCampaignFeedAd = {
	// The id of the ad
	id: string
	// The big text
	title: string

	// The smaller text under the big text
	subtitle: string

	// The location text
	location: string

	// The text above the ad
	primaryText: string

	// The large text in the bottom of the ad
	headlineText: string

	// The small text in the bottom of the ad
	descriptionText: string

	// The text of the CTA button
	ctaText: string

	// The text of the meta-level (bottom) CTA button; options set by Meta
	metaCtaText: string

	// The background image
	imageId: string

	// narrow this using the schema
	meta: unknown
}

export const feedAdDefaultMeta = {
	logoBackgroundFillColor: '#ffffff',
	backdropOverlay: {
		...metaOverlayDefault,
		fillMode: 'flat',
		fillFlatColor: '#000000',
		maskMode: 'gradient',
		maskGradientOpacity1: 0.2,
	},
	backdropOverlayProMode: true,
	backdropTransform: {
		...metaTransformDefault,
		scale: 1.2,
	},
	titleTextLayer: defaultTitleTextLayerConfiguration, // <-- this sets the title text layer config
	titleTextTransform: metaTransformDefault,
	subtitleTextLayer: defaultSubtitleTextLayerConfiguration,
	subtitleTextTransform: metaTransformDefault,
	generalContent: metaGeneralContentDefault,
	logo: metaLogoDefault,
	positioning: feedAdPositioningDefault,
	cards: feedAdCardsDefault,
	canvasEnabled: false,
	canvasDocument: null,
	forceAiGeneratedLabel: false,
} as const satisfies FeedAdMeta

// Any custom local overrides need to be present both here and the above. The above is only applied when there's no meta object
// at all; the below fallbacks get used when it's a partial fill-in.
const feedAdMetaSchema = z.object({
	logoBackgroundFillColor: colorSchema.or(fallback('#ffffff')),
	backdropOverlay: metaOverlaySchema,
	backdropOverlayProMode: z.boolean().or(fallback(false)),
	backdropTransform: metaTransformSchema,
	titleTextLayer: metaDefaultTitleTextLayerSchema, // <-- this also reflects the custom schema
	titleTextTransform: metaTransformSchema,
	subtitleTextLayer: metaDefaultSubtitleTextLayerSchema,
	subtitleTextTransform: metaTransformSchema,
	generalContent: metaGeneralContentSchema,
	logo: metaLogoSchema,
	positioning: feedAdPositioningSchema,
	cards: feedAdCardsSchema,
	canvasEnabled: z.boolean().or(fallback(false)),
	canvasDocument: z.string().nullable().or(fallback(null)),
	// Shows the label even when no image is detected as AI-generated, e.g. for externally generated uploads.
	forceAiGeneratedLabel: z.boolean().or(fallback(false)),
})

export type FeedAdMeta = z.infer<typeof feedAdMetaSchema>

export type ConformedCampaignFeedAd = Omit<UnconformedCampaignFeedAd, 'meta'> & {
	meta: FeedAdMeta
}

export function conformFeedAd(feedAd: UnconformedCampaignFeedAd): ConformedCampaignFeedAd {
	const meta = feedAd.meta

	// safeguard for "empty" input
	if (!meta || typeof meta !== 'object' || Array.isArray(meta) || Object.keys(meta).length === 0) {
		return {
			...feedAd,
			meta: feedAdDefaultMeta,
		}
	}

	// todo: if parse fails, maybe safely reset?
	return {
		...feedAd,
		meta: feedAdMetaSchema.parse(feedAd.meta),
	}
}
